import {HoistService, InitContext, XH} from '@xh/hoist/core';
import {getDefaultOptions} from '../../core/ServiceUtils';
import {AccountStructure, Deposit, Portfolio, PortfolioHistory} from '../data/DataTypes';
import {observableRef, runInAction} from '@xh/hoist/mobx';
import {uniq} from 'lodash';
import {fmtDate} from '@xh/hoist/format';

export class PortfolioService extends HoistService {
    portfoliosById = new Map<number, Portfolio>();
    @observableRef accessor accountStructure: AccountStructure;

    override async initAsync(ctx: InitContext): Promise<void> {
        console.log('initializing PortfolioService');
        await this.reloadAsync();
    }

    async reloadAsync(): Promise<void> {
        await this.refreshPortfoliosAsync();
        await this.refreshStructureAsync();
    }

    async refreshPortfoliosAsync(): Promise<void> {
        const portfolios = await this.fetchPortfoliosAsync();
        runInAction(() => {
            this.portfoliosById.clear();
            portfolios.forEach(portfolio => this.portfoliosById.set(portfolio.id, portfolio));
        });
    }

    async refreshStructureAsync(): Promise<void> {
        const accountStructure = await this.fetchAccountStructureAsync();
        runInAction(() => {
            this.accountStructure = accountStructure;
        });
    }

    get portfolios(): Portfolio[] {
        return Array.from(this.portfoliosById.values());
    }

    get brokerageNames(): string[] {
        return uniq(this.portfolios.map(p => p.brokerage));
    }

    async fetchDepositsAsync(accountId: number): Promise<Deposit[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/deposits/byaccount/${accountId}`,
            ...getDefaultOptions()
        });
    }

    async saveDepositAsync(deposit: Deposit): Promise<Deposit> {
        if (deposit.id > 0) {
            return XH.fetchService.putJson({
                url: `${XH.getConf('svcApiUrl')}/deposits/${deposit.id}`,
                body: {...deposit, asOfDate: fmtDate(deposit.asOfDate)},
                ...getDefaultOptions()
            });
        } else {
            return XH.fetchService.postJson({
                url: `${XH.getConf('svcApiUrl')}/deposits`,
                body: {...deposit, asOfDate: fmtDate(deposit.asOfDate)},
                ...getDefaultOptions()
            });
        }
    }

    async deleteDepositAsync(id: number): Promise<void> {
        await XH.fetchService.fetch({
            url: `${XH.getConf('svcApiUrl')}/deposits/${id}`,
            method: 'delete',
            ...getDefaultOptions()
        });
    }

    private async fetchAccountStructureAsync(): Promise<AccountStructure> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/accountStructure`,
            ...getDefaultOptions()
        });
    }

    async saveAccountStructureAsync(accountStructure: AccountStructure) {
        await XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/accountStructure`,
            body: accountStructure,
            ...getDefaultOptions()
        });
    }

    async fetchPortfolioHistoryAsync(portfolioId: number, range: string): Promise<PortfolioHistory[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/${portfolioId}/history`,
            params: {range},
            ...getDefaultOptions()
        });
    }

    async fetchAllPortfolioHistoryAsync(): Promise<PortfolioHistory[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/history`,
            ...getDefaultOptions()
        });
    }

    async fetchPortfolioHasDependentsAsync(portfolioId: number): Promise<boolean> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios/${portfolioId}/hasDependents`,
            ...getDefaultOptions()
        });
    }

    async savePortfolioAsync(portfolio: Portfolio): Promise<void> {
        const updated = await XH.fetchService.postJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios`,
            body: portfolio,
            ...getDefaultOptions()
        });
        portfolio.id = updated.id;
    }

    async deletePortfolioAsync(id: number): Promise<void> {
        await XH.fetchService.fetch({
            url: `${XH.getConf('svcApiUrl')}/portfolios/${id}`,
            method: 'delete',
            ...getDefaultOptions()
        });
    }

    private async fetchPortfoliosAsync(): Promise<Portfolio[]> {
        return XH.fetchService.getJson({
            url: `${XH.getConf('svcApiUrl')}/portfolios`,
            ...getDefaultOptions()
        });
    }
}
